import type { FC } from 'react';

import { faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface MicLoadingStateProps {
  className?: string;
  iconSize?: 'sm' | 'md' | 'lg';
  iconContainerClassName?: string;
}

export const MicLoadingState: FC<MicLoadingStateProps> = ({
  className = '',
  iconSize = 'md',
  iconContainerClassName,
}) => {
  // Size configurations for icon and circles
  // Three circles (outer pulse + middle + inner with icon) for visual consistency
  // Icon sizes are configurable per variant for flexibility
  const sizeConfig = {
    sm: {
      container: 'vai-w-16 vai-h-16',
      outerCircle: 'vai-w-14 vai-h-14',
      middleCircle: 'vai-w-12 vai-h-12',
      innerCircle: 'vai-w-10 vai-h-10',
      iconContainer: 'vai-w-4 vai-h-4',
      iconFontSize: '0.5rem', // 8px
    },
    md: {
      container: 'vai-w-32 vai-h-32',
      outerCircle: 'vai-w-28 vai-h-28',
      middleCircle: 'vai-w-24 vai-h-24',
      innerCircle: 'vai-w-20 vai-h-20',
      iconContainer: 'vai-w-6 vai-h-6',
      iconFontSize: '0.75rem', // 12px - smaller for extension
    },
    lg: {
      container: 'vai-w-40 vai-h-40',
      outerCircle: 'vai-w-36 vai-h-36',
      middleCircle: 'vai-w-30 vai-h-30',
      innerCircle: 'vai-w-24 vai-h-24',
      iconContainer: 'vai-w-8 vai-h-8',
      iconFontSize: '1rem', // 16px
    },
  };

  const sizes = sizeConfig[iconSize];
  return (
    <div
      className={`vai-flex vai-items-center vai-gap-4 vai-p-6 vai-bg-white ${className}`}
    >
      {/* Loading Microphone Icon */}
      <div
        className={`vai-relative vai-flex-shrink-0 ${iconContainerClassName || ''}`}
      >
        <div
          className={`vai-relative ${sizes.container} vai-flex vai-items-center vai-justify-center`}
        >
          {/* Outer ring - light blue with pulse */}
          <div
            className={`vai-absolute ${sizes.outerCircle} vai-rounded-full vai-bg-blue-200 vai-animate-pulse`}
            style={{ animationDuration: '1.5s' }}
          ></div>
          {/* Middle ring - medium blue with pulse */}
          <div
            className={`vai-absolute ${sizes.middleCircle} vai-rounded-full vai-bg-blue-300 vai-animate-pulse`}
            style={{ animationDuration: '1.5s', animationDelay: '0.3s' }}
          ></div>
          {/* Inner circle - dark blue, contains icon */}
          <div
            className={`vai-relative ${sizes.innerCircle} vai-rounded-full vai-bg-blue-500 vai-flex vai-items-center vai-justify-center vai-z-10`}
          >
            <div
              className={`${sizes.iconContainer} vai-flex vai-items-center vai-justify-center`}
            >
              <FontAwesomeIcon
                icon={faMicrophone}
                className="vai-text-white"
                style={{ fontSize: sizes.iconFontSize }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Loading Status Text */}
      <div className="vai-flex vai-flex-col vai-gap-1">
        <p className="vai-font-bold vai-text-lg vai-text-gray-900">
          Setting up microphone
        </p>
        <p className="vai-text-sm vai-text-gray-600">Preparing for note</p>
      </div>
    </div>
  );
};
